import { z } from "zod";
import {
  OrderStatus,
  OrderSourceType,
  ParseMethod,
  WaypointAction,
  WaypointRequester,
} from "@carry/shared";
import { PublicIdSchema, TimeOfDaySchema, isTimeOfDay } from "./common";
import { UnavailableTimeWindow, UnavailableTimeWindowInput } from "./pickup-spot";

export const OrderItem = z.object({
  product_name: z.string(),
  quantity: z.number().int().min(1),
  unit_price: z.number().int().nullable().default(null),
  weight_kg: z.number().nullable().default(null),
});

const RequiredOrderNumber = z.string().trim().min(1, "注文番号は必須です").max(50);

/**
 * 集荷時刻は配送時刻より前でなければならない.
 * 両方が "HH:MM" として読める場合のみ比較し、`pickup_time_start >= delivery_time_start` を弾く.
 * どちらか未入力・形式不正ならスルー (形式は `TimeOfDaySchema` が名指しで弾く. #751).
 */
function refinePickupBeforeDelivery(
  input: { pickup_time_start?: string | null; delivery_time_start?: string | null },
  ctx: z.RefinementCtx,
): void {
  const pickup = input.pickup_time_start;
  const delivery = input.delivery_time_start;
  if (!isTimeOfDay(pickup) || !isTimeOfDay(delivery)) return;
  if (pickup >= delivery) {
    ctx.addIssue({
      code: "custom",
      message: "集荷時刻は配送時刻より前に設定してください",
      path: ["pickup_time_start"],
    });
  }
}

function refineSameDayWindow(
  input: Record<string, unknown>,
  startKey: "pickup_time_start" | "delivery_time_start",
  endKey: "pickup_time_end" | "delivery_time_end",
  ctx: z.RefinementCtx,
): void {
  const start = input[startKey];
  const end = input[endKey];
  // 形式不正な値は比較しない (#751). `"9:20" > "10:00"` になるため、形式エラーに
  // 「0時をまたぐ時間帯」が重なって出て直すべき項目が読めなくなる.
  if (!isTimeOfDay(start) || !isTimeOfDay(end) || start <= end) {
    return;
  }
  ctx.addIssue({
    code: "custom",
    message: "0時をまたぐ時間帯は設定できません",
    path: [endKey],
  });
}

function refineOrderTimes(
  input: {
    pickup_time_start?: string | null;
    pickup_time_end?: string | null;
    delivery_time_start?: string | null;
    delivery_time_end?: string | null;
    pickup_sequence?: number | null;
    waypoints?: { sequence: number }[];
  },
  ctx: z.RefinementCtx,
): void {
  refinePickupBeforeDelivery(input, ctx);
  refineSameDayWindow(input, "pickup_time_start", "pickup_time_end", ctx);
  refineSameDayWindow(input, "delivery_time_start", "delivery_time_end", ctx);
  refineWaypointSequences(input, ctx);
}

/**
 * 経由地 (自社倉庫への立寄り等)。order_locations(kind='waypoint') に対応する (#722)。
 *
 * `sequence` は「注文内の訪問順の通し番号」で、集荷の訪問順 (`pickup_sequence`) と
 * 同じ土俵に並ぶ。kind ごとの連番にすると経由地が集荷の前か後かを表せず、
 * パターンB (自社倉庫で積込 → 取引先の集荷先でピック → 納品) が落ちる (#721)。
 * 納品は常に最後に訪問するため `sequence` を持たない。
 */
export const OrderWaypoint = z.object({
  /** 経由地の公開ID。order.get のみ設定され、作成・更新入力では省略できる。 */
  public_id: PublicIdSchema.nullable().default(null),
  /** 注文内の訪問順 (1 始まり)。集荷と経由地で 1..N の連番を分け合う。 */
  sequence: z.number().int().min(1),
  /** この経由地での作業 (積む / 降ろす / 立ち寄るだけ)。 */
  action: WaypointAction,
  /** 経由地の依頼元。省略した既存入力は API 保存時に自社都合へ補う (#808)。 */
  requester: WaypointRequester.optional(),
  /** 集荷拠点マスタから選んだ場合の参照 (pickup_spots.public_id)。 */
  pickup_spot_public_id: PublicIdSchema.nullable().default(null),
  name: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
  /** 立寄り可能時間帯 ("HH:MM")。指定がなければ null。 */
  time_start: TimeOfDaySchema.nullable().default(null),
  time_end: TimeOfDaySchema.nullable().default(null),
  /** この経由地での作業時間 (分)。 */
  duration_minutes: z.number().int().nullable().default(null),
  /** 地点備考 (order_locations.location_note)。 */
  note: z.string().nullable().default(null),
});

/**
 * 集荷と経由地の訪問順が 1..N の連番になっていることを検証する。
 *
 * 番号が飛んだり重なったりしていると「どちらが先か」は決まっても
 * 「間に何かが抜けているのでは」を後段 (UI / optimizer) が判断できないため、
 * 入り口で連番に正規化させる。納品は常に最後なので検証対象に含めない。
 */
function refineWaypointSequences(
  input: { pickup_sequence?: number | null; waypoints?: { sequence: number }[] },
  ctx: z.RefinementCtx,
): void {
  const waypoints = input.waypoints ?? [];
  if (waypoints.length === 0) return;
  const sequences = [input.pickup_sequence ?? 1, ...waypoints.map((w) => w.sequence)].sort(
    (a, b) => a - b,
  );
  const expected = sequences.map((_, i) => i + 1);
  if (sequences.some((value, i) => value !== expected[i])) {
    ctx.addIssue({
      code: "custom",
      message: `訪問順は集荷と経由地で 1〜${sequences.length} の連番にしてください`,
      path: ["waypoints"],
    });
  }
}

export const Order = z.object({
  id: PublicIdSchema,
  order_number: z.string().nullable(),
  client_id: PublicIdSchema.nullable(),
  /** 取引先名 (clients.name のスナップショット表示用. 一覧で参照). client_id 未設定時は null. */
  client_name: z.string().nullable(),
  delivery_spot_public_id: PublicIdSchema.nullable(),
  pickup_spot_public_id: PublicIdSchema.nullable(),
  payment_number: z.string().nullable(),
  /**
   * 支払区分 (自由入力テキスト)。取込 PDF 由来の原文をそのまま保持するため列挙しない
   * (「一括請求（後日請求）」「クレジットカード配達時 JCB」等、取引先ごとに表記が異なる)。
   * 確定時の必須チェックは order-confirmation の空欄判定が担う。
   */
  payment_type: z.string().nullable(),
  delivery_address: z.string(),
  delivery_date: z.iso.date(),
  delivery_time_start: z.string().nullable(),
  delivery_time_end: z.string().nullable(),
  delivery_building: z.string().nullable(),
  handover_location: z.string().nullable(),
  arrival_instructions: z.string().nullable(),
  delivery_company: z.string().nullable(),
  delivery_branch: z.string().nullable(),
  delivery_person_name: z.string().nullable(),
  delivery_person_name_kana: z.string().nullable(),
  delivery_email: z.string().nullable(),
  delivery_phone: z.string().nullable(),
  store_name: z.string().nullable(),
  store_address: z.string().nullable(),
  store_phone: z.string().nullable(),
  pickup_time_start: z.string().nullable(),
  pickup_time_end: z.string().nullable(),
  pickup_unavailable_times: z.array(UnavailableTimeWindow),
  /** 集荷所要時間 (分). order_locations(kind=pickup).duration_minutes に対応. */
  pickup_duration_minutes: z.number().int().nullable(),
  /** 集荷の訪問順 (1 始まり). 経由地より後ろなら 2 以上になる (#722). */
  pickup_sequence: z.number().int().min(1),
  /** 経由地 (自社倉庫への立寄り等). 訪問順の昇順で返る. 経由地なしの注文では空配列 (#722). */
  waypoints: z.array(OrderWaypoint).default([]),
  delivery_packaging: z.string().nullable(),
  store_message: z.string().nullable(),
  delivery_notes: z.string().nullable(),
  day_of_contact_person: z.string().nullable(),
  day_of_contact_phone: z.string().nullable(),
  delivery_duration_minutes: z.number().int().nullable(),
  uploaded_file_public_id: PublicIdSchema.nullable(),
  status: OrderStatus,
  source_type: OrderSourceType,
  parse_method: ParseMethod.nullable(),
  remarks: z.string().nullable(),
  /**
   * この注文だけの積載時間の上限 (分)。null = システム設定の既定 (3 時間ルール, #767)。
   * 弁当以外 (ドライアイス等) の案件で 5 時間へ延長するために受注入力で人が切り替える。
   */
  max_ride_minutes: z.number().int().nullable(),
  delivery_unavailable_times: z.array(UnavailableTimeWindow),
  items: z.array(OrderItem).default([]),
  /** ジオコーディング結果. 編集画面で地図ピンの初期位置として使う. */
  pickup_latitude: z.number().nullable(),
  pickup_longitude: z.number().nullable(),
  delivery_latitude: z.number().nullable(),
  delivery_longitude: z.number().nullable(),
  /** インライン座標または納品先マスタ座標により、自動最適化で使う配送先座標が解決できるか。 */
  delivery_coordinates_resolved: z.boolean(),
  /**
   * #489: 確定済み (confirmed/started/completed) レーンに割当済みか (order.get のみ設定)。
   * true の受注はキャンセル・削除できない (先にルート作成画面で確定解除する)。
   */
  on_locked_route: z.boolean().optional(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const CreateOrderInput = z
  .object({
    order_number: RequiredOrderNumber,
    client_id: PublicIdSchema.optional(),
    delivery_spot_public_id: PublicIdSchema.optional(),
    /** 集荷先 (pickup_spots.public_id). 取込画面でマスタ候補から選択 */
    pickup_spot_public_id: PublicIdSchema.optional(),
    payment_number: z.string().optional(),
    payment_type: z.string().optional(),
    delivery_address: z.string().min(1),
    delivery_date: z.iso.date(),
    delivery_time_start: TimeOfDaySchema.optional(),
    delivery_time_end: TimeOfDaySchema.optional(),
    delivery_building: z.string().optional(),
    handover_location: z.string().optional(),
    arrival_instructions: z.string().optional(),
    delivery_company: z.string().optional(),
    delivery_branch: z.string().optional(),
    delivery_person_name: z.string().optional(),
    delivery_person_name_kana: z.string().optional(),
    delivery_email: z.string().optional(),
    delivery_phone: z.string().optional(),
    store_name: z.string().optional(),
    store_address: z.string().optional(),
    store_phone: z.string().optional(),
    pickup_time_start: TimeOfDaySchema.optional(),
    pickup_time_end: TimeOfDaySchema.optional(),
    pickup_unavailable_times: z.array(UnavailableTimeWindowInput).optional(),
    pickup_duration_minutes: z.number().int().nullable().optional(),
    /** 集荷の訪問順 (省略時は 1 = 先頭). 経由地を集荷より前に置く場合は 2 以上 (#722). */
    pickup_sequence: z.number().int().min(1).optional(),
    /** 経由地 (自社倉庫への立寄り等). 省略時は経由地なし (#722). */
    waypoints: z.array(OrderWaypoint).optional(),
    delivery_packaging: z.string().optional(),
    store_message: z.string().optional(),
    delivery_notes: z.string().optional(),
    day_of_contact_person: z.string().optional(),
    day_of_contact_phone: z.string().optional(),
    delivery_duration_minutes: z.number().int().nullable().optional(),
    items: z.array(OrderItem).default([]),
    remarks: z.string().optional(),
    /**
     * 積載時間の上限 (分) の上書き。省略・null はシステム設定の既定 (3 時間) を使う (#767)。
     * 注文書からは判別できないため、取込は常に既定 (未指定) で入り、人が後から切り替える。
     */
    max_ride_minutes: z.number().int().min(1).nullable().optional(),
    delivery_unavailable_times: z.array(UnavailableTimeWindowInput).optional(),
    /** 取込画面でプレビューから確定した注文書 (uploaded_files.public_id). 指定時は order とリンク */
    uploaded_file_public_id: PublicIdSchema.optional(),
    /** 集荷先未選択時のジオコーディング結果 */
    pickup_latitude: z.number().nullable().optional(),
    pickup_longitude: z.number().nullable().optional(),
    /** お客様未選択時のジオコーディング結果 */
    delivery_latitude: z.number().nullable().optional(),
    delivery_longitude: z.number().nullable().optional(),
  })
  .superRefine(refineOrderTimes);

export const ListOrdersInput = z.object({
  delivery_date: z.iso.date().optional(),
  status: OrderStatus.optional(),
  /** 重複取込検出用の完全一致フィルタ (F02 重複警告) */
  order_number: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(20),
  cursor: z.string().optional(),
  // z.coerce.boolean() は "false" を true 扱いするため preprocess で解釈する。
  /** true の場合 items を含めて返す (デフォルト: false = items は空配列) */
  include_items: z
    .preprocess((v) => (typeof v === "string" ? v === "true" : v), z.boolean())
    .optional()
    .default(false),
});

export const UpdateOrderStatusInput = z.object({
  status: OrderStatus,
});

export const UpdateOrderInput = z
  .object({
    order_number: RequiredOrderNumber.optional(),
    client_id: PublicIdSchema.nullable().optional(),
    delivery_spot_public_id: PublicIdSchema.nullable().optional(),
    pickup_spot_public_id: PublicIdSchema.nullable().optional(),
    payment_number: z.string().nullable().optional(),
    payment_type: z.string().nullable().optional(),
    delivery_address: z.string().min(1),
    delivery_date: z.iso.date(),
    delivery_time_start: TimeOfDaySchema.nullable().optional(),
    delivery_time_end: TimeOfDaySchema.nullable().optional(),
    delivery_building: z.string().nullable().optional(),
    handover_location: z.string().nullable().optional(),
    arrival_instructions: z.string().nullable().optional(),
    delivery_company: z.string().nullable().optional(),
    delivery_branch: z.string().nullable().optional(),
    delivery_person_name: z.string().nullable().optional(),
    delivery_person_name_kana: z.string().nullable().optional(),
    delivery_email: z.string().nullable().optional(),
    delivery_phone: z.string().nullable().optional(),
    store_name: z.string().nullable().optional(),
    store_address: z.string().nullable().optional(),
    store_phone: z.string().nullable().optional(),
    pickup_time_start: TimeOfDaySchema.nullable().optional(),
    pickup_time_end: TimeOfDaySchema.nullable().optional(),
    pickup_unavailable_times: z.array(UnavailableTimeWindowInput).nullable().optional(),
    pickup_duration_minutes: z.number().int().nullable().optional(),
    /** 集荷の訪問順 (省略時は 1 = 先頭). 経由地を集荷より前に置く場合は 2 以上 (#722). */
    pickup_sequence: z.number().int().min(1).optional(),
    /** 経由地. 送られた配列がその注文の経由地の全量になる (差分ではなく総入れ替え) (#722). */
    waypoints: z.array(OrderWaypoint).optional(),
    delivery_packaging: z.string().nullable().optional(),
    store_message: z.string().nullable().optional(),
    delivery_notes: z.string().nullable().optional(),
    day_of_contact_person: z.string().nullable().optional(),
    day_of_contact_phone: z.string().nullable().optional(),
    delivery_duration_minutes: z.number().int().nullable().optional(),
    items: z.array(OrderItem),
    remarks: z.string().nullable().optional(),
    /** 積載時間の上限 (分) の上書き。null で既定 (3 時間) へ戻す (#767)。 */
    max_ride_minutes: z.number().int().min(1).nullable().optional(),
    delivery_unavailable_times: z.array(UnavailableTimeWindowInput).nullable().optional(),
    status: OrderStatus.optional(),
    uploaded_file_public_id: PublicIdSchema.nullable().optional(),
    /** 集荷先未選択時のジオコーディング結果 */
    pickup_latitude: z.number().nullable().optional(),
    pickup_longitude: z.number().nullable().optional(),
    /** お客様未選択時のジオコーディング結果 */
    delivery_latitude: z.number().nullable().optional(),
    delivery_longitude: z.number().nullable().optional(),
  })
  .superRefine(refineOrderTimes);

export type OrderItemDto = z.infer<typeof OrderItem>;
export type OrderWaypointDto = z.infer<typeof OrderWaypoint>;
export type OrderDto = z.infer<typeof Order>;
export type CreateOrderInputDto = z.infer<typeof CreateOrderInput>;
export type UpdateOrderInputDto = z.infer<typeof UpdateOrderInput>;
export type UpdateOrderStatusInputDto = z.infer<typeof UpdateOrderStatusInput>;

/**
 * 一括補完 (#677 / #754) が 1 リクエストで走査する受注の上限。
 * 受注一覧の取得件数 (`limit: 500`) と同じにして、画面が数えた集合と API が走査する集合を
 * 一致させる (画面が「N 件あります」と出しているのに API が別の集合を見る状態を作らない)。
 */
export const BACKFILL_PARTIAL_TIME_WINDOWS_SCAN_MAX = 500;

/**
 * 一括補完 (#677 / #754) が 1 リクエストで**更新する**上限件数。
 *
 * 1 件の補完は「`order.update` のトランザクション約 25 + `order_revisions` 1 +
 * タイムライン監査 1」で **約 27 ステートメント** (確定済みレーン判定は
 * `findLockedRouteOrderIds` で全件 1 クエリにまとめてあり件数に比例しない)。
 * Turso への各ステートメントは Workers のサブリクエスト (1 リクエストあたり 1000) に
 * 数えられるため、100 件を一度に処理すると上限を超えて失敗する。日次の受注は 20 件前後なので
 * 通常は 1 回で終わり、超えた分は `remaining_count` で返して押し直しで進められるようにする。
 */
export const BACKFILL_PARTIAL_TIME_WINDOWS_MAX = 30;

/** 片側だけ指定の配達・集荷時刻を許容幅で時間幅に補完する (#677)。 */
export const BackfillPartialTimeWindowsInput = z.object({
  /** 対象の配達日 (YYYY-MM-DD)。確定済みレーンの注文は除外する。 */
  delivery_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const BackfillPartialTimeWindowsOutput = z.object({
  data: z.object({
    /** 補完して更新した件数。 */
    updated_count: z.number().int().nonnegative(),
    /**
     * 配車済み (`assigned`)、または確定済みレーンに載っているためスキップした片側指定の件数。
     * **配車から外せば補完できる**もの。完了・キャンセルは次の一手が違うので混ぜない
     * (`skipped_finalized_count`)。
     */
    skipped_locked_count: z.number().int().nonnegative(),
    /**
     * 完了・キャンセル済みのためスキップした片側指定の件数 (#754)。
     * 終わった記録なので補完しない。運用が取れる次の一手が無いため `skipped_locked_count`
     * (配車から外す) と分けて返す。
     */
    skipped_finalized_count: z.number().int().nonnegative(),
    /** 片側指定が無かった (両側そろっている / 両側とも空) ため対象外だった件数。 */
    skipped_complete_count: z.number().int().nonnegative(),
    /**
     * 片側指定だが**許容幅では埋められない**件数 (#754)。許容幅 0、日の端で丸めて
     * 幅ゼロになる、`HH:MM` として解釈できない、など。押しても直らない受注なので
     * 「片側指定が無い」(`skipped_complete_count`) と混ぜない。
     */
    skipped_unfillable_count: z.number().int().nonnegative(),
    /**
     * 上限 (`BACKFILL_PARTIAL_TIME_WINDOWS_MAX`) を超えたため今回処理しなかった件数 (#754)。
     * 0 でなければ同じ日にもう一度実行すると続きから進む。
     */
    remaining_count: z.number().int().nonnegative(),
    /**
     * 走査上限 (`BACKFILL_PARTIAL_TIME_WINDOWS_SCAN_MAX`) に達して見ていない受注がある (#754)。
     * 件数を約束できないため真偽で返す。
     */
    scan_truncated: z.boolean(),
    order_public_ids: z.array(z.string()),
  }),
});

export type BackfillPartialTimeWindowsInputDto = z.infer<typeof BackfillPartialTimeWindowsInput>;
export type BackfillPartialTimeWindowsOutputDto = z.infer<typeof BackfillPartialTimeWindowsOutput>;

/**
 * 受注一覧からの一括確定 (#689)。1 回の呼び出しで確定できる上限件数。
 * 日常運用 (1 日 15 件程度) に対して十分な余裕を取りつつ、
 * 誤操作や自動化から DB を守るための上限として API / UI 双方で共有する。
 */
export const BULK_CONFIRM_ORDERS_MAX = 100;

/**
 * 一括確定の入力 (#689)。対象は draft → confirmed のみ。
 * 他の遷移 (確定解除・キャンセル) は取り消しが効かない/整合性チェックが増えるため単件 API に留める。
 */
export const BulkConfirmOrdersInput = z.object({
  public_ids: z.array(PublicIdSchema).min(1).max(BULK_CONFIRM_ORDERS_MAX),
});

/**
 * 確定できなかった理由の種別。
 * 表示文 (`reason`) の文字列マッチに頼らず、受注へのリンク可否など UI の分岐に使う。
 * - `not_found`: 受注が存在しない (詳細画面へのリンクを張らない)
 * - `not_draft`: 下書き以外なので一括確定の対象外
 * - `missing_fields`: 確定必須項目が不足している
 */
export const BulkConfirmFailureReasonCode = z.enum(["not_found", "not_draft", "missing_fields"]);

/** 確定できなかった 1 件の理由 (成功分はコミットし、失敗は理由付きで返す)。 */
export const BulkConfirmOrderFailure = z.object({
  public_id: z.string(),
  /** 一覧・ダイアログで人が識別するための注文番号 (未採番なら null)。 */
  order_number: z.string().nullable(),
  /** 失敗理由の種別 (UI の分岐用)。 */
  reason_code: BulkConfirmFailureReasonCode,
  /** 日本語の失敗理由 (必須項目不足・ステータス不一致・未存在)。 */
  reason: z.string(),
});

export const BulkConfirmOrdersOutput = z.object({
  data: z.object({
    confirmed_count: z.number().int().nonnegative(),
    failed_count: z.number().int().nonnegative(),
    confirmed_public_ids: z.array(z.string()),
    failed: z.array(BulkConfirmOrderFailure),
  }),
});

export type BulkConfirmOrdersInputDto = z.infer<typeof BulkConfirmOrdersInput>;
export type BulkConfirmOrdersOutputDto = z.infer<typeof BulkConfirmOrdersOutput>;
export type BulkConfirmOrderFailureDto = z.infer<typeof BulkConfirmOrderFailure>;
export type BulkConfirmFailureReasonCodeDto = z.infer<typeof BulkConfirmFailureReasonCode>;
